import { createFileRoute, Link } from '@tanstack/react-router'
import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { ArrowDown, ArrowRight } from 'lucide-react'
import { SectionHeader } from '@/components/marketing/section'
import {
  BillingSwitch,
  CheckLocations,
  CompareCell,
  formatPrice,
  FreePlanCard,
  GuaranteeStrip,
  PlanCard,
  PriceAnchor,
} from '@/components/pricing/pricing-parts'
import { pricingFaqs } from '@/components/pricing/pricing-faq'
import { useAuth } from '@/hooks/use-auth'
import { useFreePlan } from '@/hooks/use-free-plan'
import { apiV2, ApiV2Error } from '@/lib/api-v2-client'
import { openCheckout } from '@/lib/checkout'
import { FREE_PLAN } from '@/lib/free-plan'
import { faqPage, jsonLdScript, offer } from '@/lib/schema'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { annualSavingPercent, tiers } from '@/lib/plans'
import { THIRD_PARTY_PUBLIC } from '@/lib/third-party'
import { THIRD_PARTY_PRICING_SECTION, THIRD_PARTY_PRICING_TOOLTIP } from '@/components/third-party-marketing/public-copy'
import { hasPaidPlan, hasProductAccess, isAlreadySubscribed, isFreeTier } from '@/lib/subscription-access'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/pricing')({
  component: PricingPage,
  head: () => ({
    meta: [
      { title: 'Pricing — Free Plan, 30-Second Checks from $3/mo | Uptrack' },
      { name: 'description', content: 'Free plan: 5 monitors at 30s. Pro from $3/mo for 50 monitors, Team and Business for larger teams. 30-day money-back guarantee on paid plans.' },
      { property: 'og:title', content: 'Free plan and 30-second monitoring from $3/mo | Uptrack' },
      { property: 'og:description', content: 'Free: 5 monitors at 30s from one location. Pro from $3/mo, Team from $20/mo. Every paid monitor at 30s. No bait-and-switch.' },
      { property: 'og:image', content: 'https://uptrack.app/og/pricing.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/pricing' },
    ],
    scripts: [
      // The page is built for the open state: the server flag is on when the site ships.
      jsonLdScript(faqPage(pricingFaqs('open'))),
      jsonLdScript({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Uptrack Uptime Monitoring',
        description: 'Uptime monitoring with consecutive-check alert confirmation.',
        brand: { '@type': 'Brand', name: 'Uptrack' },
        offers: [
          offer({ name: 'Free', price: '0', description: '5 monitors, all at 30s checks, one check location (Nuremberg), HTTP and keyword monitors, email and one chat channel for alerts' }),
          offer({ name: 'Pro (monthly)', price: '3', description: `50 monitors, all at 30s checks, ${MONITOR_LOCATIONS.plan.schema.pro}, billed monthly`, billingDuration: 'P1M' }),
          offer({ name: 'Pro (annual)', price: '2.50', description: `50 monitors, all at 30s checks, ${MONITOR_LOCATIONS.plan.schema.pro}, $30/yr — save ${annualSavingPercent('pro')}%`, billingDuration: 'P1Y' }),
          offer({ name: 'Team (monthly)', price: '20', description: `200 monitors, 30s checks, ${MONITOR_LOCATIONS.plan.schema.team}, unlimited status pages, billed monthly`, billingDuration: 'P1M' }),
          offer({ name: 'Team (annual)', price: '15', description: `200 monitors, 30s checks, ${MONITOR_LOCATIONS.plan.schema.team}, unlimited status pages, $180/yr — save ${annualSavingPercent('team')}%`, billingDuration: 'P1Y' }),
          offer({ name: 'Business (monthly)', price: '79', description: `1,000 monitors, 30s checks, ${MONITOR_LOCATIONS.plan.schema.business}, SSO, whitelabel, billed monthly`, billingDuration: 'P1M' }),
          offer({ name: 'Business (annual)', price: '49', description: `1,000 monitors, 30s checks, ${MONITOR_LOCATIONS.plan.schema.business}, SSO, whitelabel, $588/yr — save ${annualSavingPercent('business')}%`, billingDuration: 'P1Y' }),
        ],
      }),
    ],
  }),
})

export { tiers }

const featureTooltips: Record<string, string> = {
  'Heartbeat monitors': 'Passive monitors — your service pings a unique URL. If pings stop, you get alerted.',
  'Cron job monitoring': 'Monitor scheduled tasks. Set an expected schedule and get alerted if a job misses its window.',
  'Grace period': 'Time to wait after a missed heartbeat before alerting. Prevents false alarms from brief delays.',
  'Default interval': 'How often each monitor checks your site. Every plan, free and paid, runs at 30 seconds.',
  'Check locations': MONITOR_LOCATIONS.plan.tooltip,
  'History': 'How far back you can view uptime data, response times, and incident logs.',
  'Keyword monitoring': 'Check that a specific word or phrase appears (or doesn\'t appear) in the response body.',
  'Alert channels': 'Services that receive alerts when incidents happen. Paid plans include Email, Slack, Discord, Telegram and signed webhooks. The free plan includes email and one chat channel (Discord, Slack or Telegram).',
  'Weekly reports': 'Automated email every Monday with uptime summary, response time trends, and incident count.',
  'Escalation policies': 'Automatically escalate unacknowledged alerts to secondary contacts after a delay.',
  'Maintenance scheduling': 'Plan downtime windows that suppress alerts and show a banner on your status page.',
  'Custom domain': 'Host your public status page on your own domain (e.g. status.yourcompany.com).',
  'Status page theming': 'Customize colors, fonts, and branding on your public status page.',
  'Subscriber notifications': 'Let your users subscribe to status page updates via email.',
  'Whitelabel': 'Remove "Powered by Uptrack" branding from your public status pages.',
  'Custom email sender': 'Send alert emails from your own domain (e.g. alerts@yourcompany.com) instead of alerts@uptrack.app.',
  'Notify-only seats': 'Team members who receive alerts and can view dashboards, but cannot create or modify monitors.',
  '2FA (TOTP)': 'Two-factor authentication using authenticator apps like Google Authenticator or Authy.',
  'SSO / SAML': 'Single Sign-On via SAML 2.0. Let your team log in with Okta, Azure AD, or other identity providers.',
  'Data export (CSV)': 'Export monitor checks, incidents, and uptime data as CSV files.',
}

type FeatureRow = { feature: string; free: string; pro: string; team: string; business: string }
type FeatureSection = { section: string; rows: FeatureRow[] }

const NO = '—'

const comparisonSections: FeatureSection[] = [
  {
    section: 'Uptime Monitoring',
    rows: [
      { feature: 'Monitors', free: String(FREE_PLAN.monitors), pro: '50', team: '200', business: '1,000' },
      { feature: 'Default interval', free: `${FREE_PLAN.intervalSeconds} sec`, pro: '30 sec', team: '30 sec', business: '30 sec' },
      { feature: 'Check locations', free: `1 (${FREE_PLAN.location})`, pro: MONITOR_LOCATIONS.plan.tableCell.pro, team: MONITOR_LOCATIONS.plan.tableCell.team, business: MONITOR_LOCATIONS.plan.tableCell.business },
      { feature: 'History', free: `${FREE_PLAN.historyDays} days`, pro: '2 years', team: '2 years', business: '5 years' },
      { feature: 'Keyword monitoring', free: 'Yes', pro: 'Yes', team: 'Yes', business: 'Yes' },
      { feature: 'SSL / TCP / DNS / Ping', free: NO, pro: 'Yes', team: 'Yes', business: 'Yes' },
    ],
  },
  {
    section: 'Cron & Heartbeat Monitoring',
    rows: [
      { feature: 'Cron & heartbeat monitors', free: NO, pro: 'Included (shared pool)', team: 'Included (shared pool)', business: 'Included (shared pool)' },
      { feature: 'Grace period', free: NO, pro: 'Configurable', team: 'Configurable', business: 'Configurable' },
    ],
  },
  {
    section: 'Browser Monitoring',
    rows: [
      { feature: 'Browser monitors (Playwright)', free: NO, pro: '5', team: '20', business: 'Unlimited' },
      { feature: 'Browser check frequency', free: NO, pro: 'Every 10 min', team: 'Every 5 min', business: 'Every 1 min' },
      { feature: 'Per-step screenshots & replay', free: NO, pro: 'Yes', team: 'Yes', business: 'Yes' },
      { feature: 'Visual regression', free: NO, pro: 'Yes', team: 'Yes', business: 'Yes' },
    ],
  },
  {
    section: 'Alerts & Reports',
    rows: [
      { feature: 'Alert channels', free: 'Email + 1 chat channel', pro: 'Email, Slack, Discord, Telegram, Webhook', team: 'Email, Slack, Discord, Telegram, Webhook', business: 'Email, Slack, Discord, Telegram, Webhook' },
      { feature: 'Weekly reports', free: NO, pro: NO, team: 'Yes', business: 'Yes' },
      { feature: 'Escalation policies', free: NO, pro: NO, team: 'Yes', business: 'Yes' },
      { feature: 'Maintenance scheduling', free: NO, pro: NO, team: 'Yes', business: 'Yes' },
    ],
  },
  {
    section: 'Status Pages',
    rows: [
      { feature: 'Status pages', free: String(FREE_PLAN.statusPages), pro: '5', team: 'Unlimited', business: 'Unlimited' },
      { feature: 'Custom domain', free: NO, pro: NO, team: 'Yes', business: 'Yes' },
      { feature: 'Status page theming', free: NO, pro: NO, team: 'Yes', business: 'Yes' },
      { feature: 'Subscriber notifications', free: NO, pro: 'Yes', team: 'Yes', business: 'Yes' },
      { feature: 'Whitelabel', free: NO, pro: NO, team: NO, business: 'Yes' },
      { feature: 'Custom email sender', free: NO, pro: NO, team: NO, business: 'Yes' },
    ],
  },
  {
    section: 'Team & Account',
    rows: [
      { feature: 'Team members', free: String(FREE_PLAN.teamMembers), pro: '3', team: '5', business: '15' },
      { feature: 'Notify-only seats', free: NO, pro: '1', team: '3', business: '5' },
      { feature: '2FA (TOTP)', free: 'Yes', pro: 'Yes', team: 'Yes', business: 'Yes' },
      { feature: 'SSO / SAML', free: NO, pro: NO, team: NO, business: 'Yes' },
      { feature: 'Audit logs', free: NO, pro: 'Yes', team: 'Yes', business: 'Yes' },
      { feature: 'API access', free: NO, pro: 'Yes', team: 'Yes', business: 'Yes' },
      { feature: 'Data export (CSV)', free: NO, pro: 'Yes', team: 'Yes', business: 'Yes' },
    ],
  },
]

/** The comparison table. The third party section shows only when THIRD_PARTY_PUBLIC is true. */
export function visibleComparisonSections(): FeatureSection[] {
  if (!THIRD_PARTY_PUBLIC) return comparisonSections
  const at = comparisonSections.findIndex((s) => s.section === 'Browser Monitoring')
  return [...comparisonSections.slice(0, at), THIRD_PARTY_PRICING_SECTION, ...comparisonSections.slice(at)]
}

/** The tooltip of a comparison row, or undefined. */
function tooltipFor(feature: string): string | undefined {
  return featureTooltips[feature] ?? (THIRD_PARTY_PUBLIC ? THIRD_PARTY_PRICING_TOOLTIP[feature] : undefined)
}

export function PricingPage() {
  const { organization } = useAuth()
  const currentPlan = organization?.plan ?? 'free'
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null)
  const [isAnnual, setIsAnnual] = useState(true)
  // While the state loads, and when it cannot be read, the plan counts as open.
  const freePlan = useFreePlan()
  const showFree = !freePlan.isClosed
  const faqs = pricingFaqs(freePlan.state)

  const { data: popularData } = useQuery({
    queryKey: ['popular-plan'],
    queryFn: () => apiV2.getPopularPlan(),
    staleTime: 1000 * 60 * 30,
  })
  const popularPlan = tiers.find(tier => tier.plan === popularData?.plan)?.plan ?? 'pro'

  async function handleCheckout(plan: string) {
    if (!organization) {
      window.location.assign(`/signup?plan=${plan}&interval=${isAnnual ? 'annual' : 'monthly'}`)
      return
    }

    if (plan === currentPlan) return

    // A live subscription changes plan in billing settings; a second checkout
    // would start a second subscription and bill twice (openspec 112). The free
    // plan has product access but no subscription: it goes to checkout.
    if (hasPaidPlan(organization)) {
      window.location.assign('/dashboard/settings?tab=billing')
      return
    }

    const interval = isAnnual ? 'annual' : 'monthly'
    setLoadingPlan(plan)
    try {
      const result = await apiV2.createCheckout(plan, interval)
      const successUrl = `${window.location.origin}/dashboard/settings?billing=success&plan=${plan}`
      await openCheckout(result.provider, result.checkout_url, result.transaction_id, successUrl)
      setLoadingPlan(null)
    } catch (err) {
      if (isAlreadySubscribed(err)) {
        window.location.assign('/dashboard/settings?tab=billing')
        return
      }
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to start checkout')
      setLoadingPlan(null)
    }
  }

  /** Signed out: start a plan. Signed in: change plan in billing, or check out. */
  function actionLabel(tier: (typeof tiers)[number]) {
    if (!organization) return tier.plan === 'pro' ? 'Start with Pro' : `Choose ${tier.name}`
    return hasPaidPlan(organization) ? `Switch to ${tier.name}` : `Choose ${tier.name}`
  }

  /** The button on the Free card. The plan is signed up for without checkout. */
  function freeAction() {
    if (freePlan.isFull) {
      return (
        <Button size="lg" className="w-full" variant="outline" disabled>
          Free plan is full
        </Button>
      )
    }
    if (organization && isFreeTier(organization)) {
      return (
        <Button size="lg" className="w-full" variant="outline" disabled>
          Current plan
        </Button>
      )
    }
    if (organization && hasProductAccess(organization)) {
      return (
        <Button size="lg" className="w-full" variant="outline" disabled>
          Free plan
        </Button>
      )
    }
    return (
      <Button size="lg" className="w-full" variant="outline" asChild>
        {organization ? <Link to="/dashboard">Start free</Link> : <Link to="/signup" search={{ plan: 'free' }}>Start free</Link>}
      </Button>
    )
  }

  return (
    <>

      <>
        {/* Hero: words left (5), price anchor right (7) */}
        <section className="container mx-auto px-4 pb-14 pt-12 md:pt-20">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <h1 className="text-balance text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">
                {showFree ? `30-second monitoring, free for ${freePlan.monitors} monitors` : '30-second monitoring for $3 a month'}
              </h1>
              <p className="mt-5 text-lead text-body">
                Every monitor checks every 30 seconds, on every plan.{showFree ? ' Pro is $3 a month for 50 monitors.' : ''} {MONITOR_LOCATIONS.plan.hero}
              </p>
              <div className="mt-7">
                <BillingSwitch annual={isAnnual} onChange={setIsAnnual} />
              </div>
              <p className="mt-4 text-sm text-muted-foreground">Paid plans: 30-day money-back guarantee · Prices exclude taxes</p>
            </div>
            <div className="lg:col-span-7">
              <PriceAnchor />
            </div>
          </div>
        </section>

        {/* Plan cards; the popular plan is raised */}
        <section className="container mx-auto px-4 pb-14">
          <div className={cn('grid items-stretch gap-5', showFree ? 'md:grid-cols-2 lg:grid-cols-4' : 'lg:grid-cols-3')}>
            {showFree && (
              <FreePlanCard
                stacked
                state={freePlan.state}
                facts={{ monitors: freePlan.monitors, intervalSeconds: freePlan.intervalSeconds, locationLabel: freePlan.locationLabel }}
                action={freeAction()}
              />
            )}
            {tiers.map((tier) => {
              const isCurrent = tier.plan === currentPlan
              const isLoading = loadingPlan === tier.plan
              const isPopular = tier.plan === popularPlan
              return (
                <PlanCard
                  key={tier.plan}
                  tier={tier}
                  annual={isAnnual}
                  popular={isPopular}
                  stacked={showFree}
                  action={
                    isCurrent ? (
                      <Button size="lg" className="w-full" variant="outline" disabled>
                        Current plan
                      </Button>
                    ) : (
                      <Button
                        size="lg"
                        className="w-full"
                        variant={isPopular ? 'default' : 'outline'}
                        disabled={isLoading || !!loadingPlan}
                        onClick={() => handleCheckout(tier.plan)}
                      >
                        {isLoading ? 'Redirecting...' : actionLabel(tier)}
                      </Button>
                    )
                  }
                />
              )
            })}
          </div>
          <div className="mt-6 flex justify-center">
            <Button asChild variant="outline" size="lg">
              <a href="#compare">
                Compare all features
                <ArrowDown className="ml-2 h-4 w-4" aria-hidden="true" />
              </a>
            </Button>
          </div>
          <div className="mt-6">
            <GuaranteeStrip />
          </div>
        </section>

        <CheckLocations />

        {/* Feature Comparison Table: the plan header stays pinned under the site header */}
        <section id="compare" className="scroll-mt-20 py-14 md:py-20">
          <div className="container mx-auto px-4">
            <SectionHeader align="center" title="Compare plans" className="mb-10" />
            <div className="overflow-x-auto md:overflow-visible" tabIndex={0} role="region" aria-label="Plan comparison">
              <table className="w-full text-[15px]">
                <thead className="md:sticky md:top-[84px] md:z-10">
                  <tr className="border-b border-border bg-background">
                    <th className="w-[28%] py-3 pr-4 text-left font-medium text-muted-foreground md:w-[30%]">
                      <span className="sr-only">Feature</span>
                    </th>
                    {showFree && (
                      <th className="px-3 py-3 text-center font-semibold">
                        Free
                        <div className="num text-sm font-normal text-muted-foreground">$0/mo</div>
                      </th>
                    )}
                    {tiers.map((tier) => (
                      <th key={tier.plan} className={`px-3 py-3 text-center font-semibold ${tier.plan === popularPlan ? 'text-primary' : ''}`}>
                        {tier.name}
                        <div className="num text-sm font-normal text-muted-foreground">
                          ${formatPrice(isAnnual ? tier.annualMonthlyPrice : tier.monthlyPrice)}/mo
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleComparisonSections().map((section) => (
                    <Fragment key={section.section}>
                      <tr>
                        <td colSpan={showFree ? 5 : 4} className="pb-2 pt-8 text-sm font-semibold text-foreground">
                          {section.section}
                        </td>
                      </tr>
                      {section.rows.map((row) => (
                        <tr key={row.feature} className="border-b border-border">
                          <td className="py-3 pr-4 font-medium">
                            <span className="inline-flex items-center gap-1">
                              {row.feature}
                              {tooltipFor(row.feature) && (
                                <span className="group relative">
                                  <span
                                    className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-foreground/30 text-xs text-muted-foreground"
                                  >
                                    ?
                                  </span>
                                  <span className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 w-64 rounded-lg border border-border bg-popover px-3 py-2 text-sm leading-relaxed text-popover-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100">
                                    {tooltipFor(row.feature)}
                                  </span>
                                </span>
                              )}
                            </span>
                          </td>
                          {showFree && <td className="num px-3 py-3 text-center"><CompareCell value={row.free} /></td>}
                          <td className="num px-3 py-3 text-center"><CompareCell value={row.pro} /></td>
                          <td className="num px-3 py-3 text-center"><CompareCell value={row.team} /></td>
                          <td className="num py-3 pl-3 text-center"><CompareCell value={row.business} /></td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-border py-14 md:py-20">
          <div className="container mx-auto px-4">
            <h2 className="mb-8 text-2xl font-semibold tracking-tight md:text-3xl">Frequently asked questions</h2>
            <div className="max-w-2xl space-y-6">
              {faqs.map((faq) => (
                <div key={faq.question}>
                  <h3 className="mb-2 font-medium">{faq.question}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-border py-14 md:py-20">
          <div className="container mx-auto px-4">
            <div className="grid items-center gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">Ready to stop the false alerts?</h2>
                <p className="mt-3 max-w-lg text-muted-foreground">
                  {showFree
                    ? 'Start free or choose a plan, and set up your first monitor in under a minute.'
                    : 'Choose a plan and set up your first monitor in under a minute.'}
                </p>
              </div>
              <div className="lg:col-span-5 lg:text-right">
                <Button size="lg" asChild>
                  <Link to="/signup" search={showFree && !freePlan.isFull ? { plan: 'free' } : undefined}>
                    {showFree && !freePlan.isFull ? 'Start free' : 'Get Started'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </>

      </>
  )
}
